import { useEffect, useState } from 'react';
import bannerImage from '../../../assets/images/banner.png';
import rocketBanner from '../../../assets/images/rocketbanner.png';
import { BANNER_FEATURES } from '../../../constants/homeData';
import CheckIcon from '../../../assets/images/check.svg';

function BannerSection({ banners, loading }) {
  const slides = banners?.length ? banners : null;
  const totalSlides = slides?.length || 1;
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    if (activeSlide >= totalSlides) setActiveSlide(0);
  }, [activeSlide, totalSlides]);

  const goPrev = () => setActiveSlide((i) => (i === 0 ? totalSlides - 1 : i - 1));
  const goNext = () => setActiveSlide((i) => (i === totalSlides - 1 ? 0 : i + 1));

  const current = slides?.[activeSlide];
  const primaryImage = current?.imageUrl || bannerImage;
  const secondaryImage = rocketBanner || bannerImage;
  const scrollImages = [primaryImage, secondaryImage, primaryImage, secondaryImage, primaryImage];

  return (
    <section className="px-4 pt-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <style>{`
          /* Use exact pixel distances to avoid subpixel rounding "blink" at loop boundary. */
          @keyframes chse-banner-vertical-marquee {
            0% { transform: translate3d(0, 0, 0); }
            100% { transform: translate3d(0, -800px, 0); } /* 5 items * 160px */
          }

          @media (min-width: 640px) {
            @keyframes chse-banner-vertical-marquee {
              0% { transform: translate3d(0, 0, 0); }
              100% { transform: translate3d(0, -950px, 0); } /* 5 items * 190px */
            }
          }

          @media (min-width: 1024px) {
            @keyframes chse-banner-vertical-marquee {
              0% { transform: translate3d(0, 0, 0); }
              100% { transform: translate3d(0, -1050px, 0); } /* 5 items * 210px */
            }
          }
        `}</style>
        <div className="overflow-hidden rounded-[2rem] bg-[#000000]/80 lg:rounded-[2.5rem]">
          <div className="grid items-center gap-8 p-6 sm:p-8 lg:grid-cols-2 lg:gap-4 lg:p-10 xl:p-12">
            <div className="text-white">
              <h1 className="text-3xl font-bold sm:text-4xl lg:text-[36px] xl:text-[40px]">
                {current?.title || "Odisha's No. 1*"}
                <span className="block">{current?.subtitle || 'learning Platform'}</span>
              </h1>
              <p className="mt-2 text-base font-medium text-[#E0B300]">
                Complete CHSE Preparation Anytime Anywhere
              </p>

              <ul className="mt-6 space-y-3 sm:mt-8">
                {BANNER_FEATURES.map((row) => (
                  <li key={row.left} className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:gap-10 xl:gap-14">
                    <span className="flex items-center gap-4 text-base text-[#FFF7D9]">
                      <img src={CheckIcon} alt="check" className="h-4 w-4" />
                      {row.left}
                    </span>
                    <span className="flex items-center gap-4 text-base text-[#FFF7D9]">
                      <img src={CheckIcon} alt="check" className="h-4 w-4" />
                      {row.right}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className="mx-auto mt-8 inline-flex items-center gap-0 overflow-hidden rounded-full bg-white py-1 pr-1 text-sm font-semibold text-[#000000] sm:mt-10 sm:pl-8 sm:text-base lg:mx-0 xl:mx-36"
              >
                Explore Now
                <span className="ml-4 flex h-9 w-9 items-center justify-center rounded-full bg-[#088EAC]">
                  <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </button>
            </div>

            <div className="relative flex justify-center lg:justify-center">
              {loading && !current?.imageUrl ? (
                <div className="h-[320px] w-full max-w-md animate-pulse rounded-2xl bg-white/10 sm:h-[380px]" />
              ) : (
                <div className="relative z-10 h-[320px] w-full max-w-xl overflow-hidden rounded-2xl sm:h-[380px] lg:h-[420px]">
                  <div
                    className="absolute inset-0 flex flex-col"
                    style={{
                      animation: 'chse-banner-vertical-marquee 14s linear infinite',
                      willChange: 'transform',
                    }}
                  >
                    {[0, 1].map((groupIndex) => (
                      <div key={groupIndex} className="flex flex-col">
                        {scrollImages.map((src, index) => (
                          <div
                            key={`${groupIndex}-${src}-${index}`}
                            className="flex h-[160px] w-full items-center justify-center p-2 sm:h-[190px] lg:h-[210px]"
                          >
                            <img
                              src={src}
                              alt=""
                              className="h-full w-full rounded-2xl object-cover"
                              draggable="false"
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {slides && slides.length > 1 && (
          <div className="mt-3 flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={goPrev}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition hover:border-violet-400 hover:text-violet-600"
              aria-label="Previous slide"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex items-center gap-2">
              {slides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => setActiveSlide(index)}
                  className={`h-2.5 rounded-full transition-all ${
                    activeSlide === index ? 'w-8 bg-violet-600' : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={goNext}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition hover:border-violet-400 hover:text-violet-600"
              aria-label="Next slide"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default BannerSection;
